import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api';
import Loading from '../../components/common/Loading';

interface AdminOrder {
  id: number;
  orderNumber: string;
  status: string;
  region: string;
  currency: string;
  totalAmount: number;
  createdAt: string;
  trackingNumber: string | null;
  packingVideoUrl: string | null;
  groupBuyId: number | null;
  shippingAddress: { recipientName: string; country: string; phone: string } | null;
  items: { orderItemId: number; groupBuyId: number | null; name: string; quantity: number; unitPrice: number }[];
}

interface GroupedBuy {
  groupBuyId: number | null;
  title: string;
  orders: AdminOrder[];
  totalRevenue: number;
  totalItems: number;
}

const STATUS_LABEL: Record<string, string> = {
  PENDING: '결제대기', PAID: '결제완료', PREPARING: '준비중',
  SHIPPED: '배송중', DELIVERED: '배송완료',
  CANCELLED: '취소', REFUNDED: '환불',
};

const STATUS_COLOR: Record<string, string> = {
  PENDING: '#ff9800', PAID: '#4caf50', PREPARING: '#2196f3',
  SHIPPED: '#9c27b0', DELIVERED: '#2e7d32',
  CANCELLED: '#e53935', REFUNDED: '#c2185b',
};

const STATUS_OPTIONS = ['PENDING', 'PAID', 'PREPARING', 'SHIPPED', 'DELIVERED', 'CANCELLED', 'REFUNDED'];

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const [trackingTarget, setTrackingTarget] = useState<AdminOrder | null>(null);
  const [trackingNo, setTrackingNo] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.getOrders(page).then(r => {
      setOrders(r.data.data.content);
      setTotalPages(r.data.data.totalPages);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]);

  // 공구별 그룹핑
  const grouped: GroupedBuy[] = React.useMemo(() => {
    const map = new Map<string, GroupedBuy>();

    orders.forEach(order => {
      const key = order.groupBuyId ? String(order.groupBuyId) : 'ungrouped';
      const title = order.items[0]?.name ?? '(상품명 없음)';

      if (!map.has(key)) {
        map.set(key, { groupBuyId: order.groupBuyId, title, orders: [], totalRevenue: 0, totalItems: 0 });
      }
      const g = map.get(key)!;
      g.orders.push(order);
      g.totalRevenue += Number(order.totalAmount);
      g.totalItems += order.items.reduce((s, i) => s + i.quantity, 0);
    });

    return Array.from(map.values());
  }, [orders]);

  const toggle = (key: string) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const handleStatus = async (id: number, status: string) => {
    await adminApi.updateOrderStatus(id, status);
    load();
  };

  const openTracking = (order: AdminOrder) => {
    setTrackingTarget(order);
    setTrackingNo(order.trackingNumber ?? '');
    setVideoUrl(order.packingVideoUrl ?? '');
  };

  const saveTracking = async () => {
    if (!trackingTarget || !trackingNo.trim()) {
      alert('운송장 번호를 입력하세요.');
      return;
    }
    if (!videoUrl.trim()) {
      alert('포장 영상 링크를 첨부하세요.');
      return;
    }
    setSaving(true);
    try {
      await adminApi.registerTracking(trackingTarget.id, trackingNo.trim(), videoUrl.trim() || undefined);
      setTrackingTarget(null);
      load();
    } catch (e: any) {
      alert(e.response?.data?.message ?? '저장 실패');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>주문 관리 (공구별)</h2>

      {loading ? <Loading /> : grouped.map(g => {
        const key = g.groupBuyId ? String(g.groupBuyId) : 'ungrouped';
        const isOpen = !collapsed[key];
        const paidCount = g.orders.filter(o => o.status !== 'PENDING' && o.status !== 'CANCELLED' && o.status !== 'REFUNDED').length;

        return (
          <div key={key} style={styles.group}>
            {/* 공구 헤더 */}
            <div style={styles.groupHeader} onClick={() => toggle(key)}>
              <div style={styles.groupLeft}>
                <span style={styles.groupIcon}>🛍️</span>
                <div>
                  <p style={styles.groupTitle}>{g.title}</p>
                  {g.groupBuyId && <p style={styles.groupId}>공구 #{g.groupBuyId}</p>}
                </div>
              </div>
              <div style={styles.groupStats}>
                <div style={styles.stat}>
                  <span style={styles.statNum}>{g.orders.length}</span>
                  <span style={styles.statLabel}>주문</span>
                </div>
                <div style={styles.stat}>
                  <span style={styles.statNum}>{paidCount}</span>
                  <span style={styles.statLabel}>결제완료</span>
                </div>
                <div style={styles.stat}>
                  <span style={styles.statNum}>{g.totalItems}</span>
                  <span style={styles.statLabel}>수량</span>
                </div>
                <div style={styles.stat}>
                  <span style={{ ...styles.statNum, color: '#b39ddb' }}>${g.totalRevenue.toFixed(2)}</span>
                  <span style={styles.statLabel}>매출</span>
                </div>
                <span style={{ fontSize: 18, color: '#aaa', marginLeft: 8 }}>{isOpen ? '▲' : '▼'}</span>
              </div>
            </div>

            {/* 주문 목록 */}
            {isOpen && (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.thead}>
                    {['주문번호', '주문일', '수령인', '수량', '금액', '상태', '운송장', '상태변경'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.orders.map(order => (
                    <tr key={order.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={{ fontWeight: 600, fontSize: 12 }}>#{order.id}</div>
                        <div style={{ fontSize: 10, color: '#bbb' }}>{order.orderNumber}</div>
                      </td>
                      <td style={styles.td}>{order.createdAt.slice(0, 10)}</td>
                      <td style={styles.td}>
                        <div>{order.shippingAddress?.recipientName ?? '-'}</div>
                        <div style={{ fontSize: 11, color: '#aaa' }}>{order.shippingAddress?.phone ?? ''}</div>
                      </td>
                      <td style={styles.td}>{order.items.reduce((s, i) => s + i.quantity, 0)}개</td>
                      <td style={styles.td}>${Number(order.totalAmount).toFixed(2)}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: STATUS_COLOR[order.status] + '22', color: STATUS_COLOR[order.status] }}>
                          {STATUS_LABEL[order.status] ?? order.status}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {order.trackingNumber ? (
                          <div>
                            <div style={{ fontSize: 11, color: '#888' }}>우체국</div>
                            <div style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 600 }}>{order.trackingNumber}</div>
                            <button style={styles.trackBtn} onClick={() => openTracking(order)}>수정</button>
                          </div>
                        ) : ['CANCELLED', 'REFUNDED'].includes(order.status) ? (
                          <span style={{ color: '#ccc', fontSize: 12 }}>-</span>
                        ) : order.status === 'PAID' ? (
                          <button style={styles.prepareBtn} onClick={() => handleStatus(order.id, 'PREPARING')}>
                            준비
                          </button>
                        ) : order.status === 'PREPARING' ? (
                          <button style={styles.addTrackBtn} onClick={() => openTracking(order)}>
                            + 운송장 등록
                          </button>
                        ) : (
                          <span style={{ color: '#ccc', fontSize: 12 }}>-</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <select value={order.status} onChange={e => handleStatus(order.id, e.target.value)} style={styles.select}>
                          {STATUS_OPTIONS
                            .filter(s => !(s === 'CANCELLED' && ['PREPARING', 'SHIPPED', 'DELIVERED'].includes(order.status)))
                            .map(s => <option key={s} value={s}>{STATUS_LABEL[s] ?? s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 24 }}>
          {Array.from({ length: totalPages }, (_, i) => (
            <button key={i} onClick={() => setPage(i)}
              style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #e8e8e8', background: page === i ? '#b39ddb' : '#fff', color: page === i ? '#fff' : '#555', cursor: 'pointer' }}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {trackingTarget && (
        <div style={styles.backdrop}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>운송장 등록 (우체국)</h3>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>주문 #{trackingTarget.id} · {trackingTarget.shippingAddress?.recipientName}</p>
            <label style={styles.label}>운송장 번호</label>
            <input style={styles.input} value={trackingNo} onChange={e => setTrackingNo(e.target.value)} placeholder="EMS/등기 번호 (예: EE123456785KR)" />
            <label style={{ ...styles.label, marginTop: 16 }}>포장 영상 URL (필수)</label>
            <input style={styles.input} value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="유튜브 등 영상 링크 (https://...) — 필수" />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button style={styles.cancelBtn} onClick={() => setTrackingTarget(null)}>취소</button>
              <button style={styles.saveBtn} onClick={saveTracking} disabled={saving}>{saving ? '저장중...' : '저장'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  group: { marginBottom: 16, border: '1px solid #e8e8e8', borderRadius: 12, overflow: 'hidden' },
  groupHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: '#fafafa', cursor: 'pointer', userSelect: 'none' },
  groupLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  groupIcon: { fontSize: 24 },
  groupTitle: { fontSize: 15, fontWeight: 700, color: '#333' },
  groupId: { fontSize: 11, color: '#aaa', marginTop: 2 },
  groupStats: { display: 'flex', alignItems: 'center', gap: 24 },
  stat: { textAlign: 'center' },
  statNum: { display: 'block', fontSize: 18, fontWeight: 800, color: '#333' },
  statLabel: { fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  thead: { background: '#fff' },
  th: { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#aaa', borderBottom: '1px solid #f0f0f0' },
  tr: { borderBottom: '1px solid #f9f9f9' },
  td: { padding: '10px 14px', color: '#333', verticalAlign: 'middle' },
  badge: { padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
  select: { padding: '4px 8px', border: '1px solid #e8e8e8', borderRadius: 6, fontSize: 12 },
  trackBtn: { marginTop: 4, padding: '2px 8px', background: 'none', border: '1px solid #e8e8e8', borderRadius: 4, fontSize: 10, color: '#888', cursor: 'pointer' },
  addTrackBtn: { padding: '6px 12px', background: '#fff', color: '#b39ddb', border: '1px dashed #b39ddb', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  prepareBtn: { padding: '6px 14px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#fff', borderRadius: 12, padding: 28, width: 400, maxWidth: '90%' },
  label: { display: 'block', fontSize: 12, color: '#555', fontWeight: 600, marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  cancelBtn: { padding: '10px 16px', background: '#f5f5f5', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  saveBtn: { padding: '10px 20px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

export default AdminOrders;
